import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Save, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const GROUP_LABELS = {
  talk: "Servicios de consultor",
  diagnostics: "Diagnósticos",
  academy: "Academia",
  marketplace: "Marketplace",
  accompaniment: "Acompañamientos",
  offerings: "Templo Vivo · Ofrendas",
  games: "Templo Vivo · Juegos",
};

const CostRow = ({ row, onSaved }) => {
  const [value, setValue] = useState(String(row.energy_cost ?? 0));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => setValue(String(row.energy_cost ?? 0)), [row.energy_cost]);

  const save = async () => {
    const next = Number(value);
    if (!Number.isInteger(next) || next < 0) {
      toast.error("El costo debe ser un número entero de Energía KD.");
      return;
    }
    if (reason.trim().length < 3) {
      toast.error("Indica un motivo para el cambio.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(
        `/api/director/costs/${row.domain}/${encodeURIComponent(row.id)}`,
        { energy_cost: next, reason: reason.trim() },
      );
      setReason("");
      toast.success(`Costo actualizado: ⚡ ${next.toLocaleString("es-CO")} KD`);
      await onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const meta = [
    row.status,
    row.archived ? "Archivado" : null,
    row.is_free ? "Gratuito" : null,
    row.active === false ? "Inactivo" : null,
  ].filter(Boolean).join(" · ");

  return (
    <article className="rounded-2xl border border-white/10 bg-white/[.025] p-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_170px_minmax(180px,280px)_auto] lg:items-end">
        <div className="min-w-0">
          <div className="font-cinzel text-base text-[#ead6aa]">{row.title}</div>
          <div className="mt-1 font-mont text-[9px] uppercase tracking-[.14em] text-white/45">
            {row.id}{meta ? ` · ${meta}` : ""}
          </div>
          {row.creator_base_energy > 0 && (
            <div className="mt-1 font-mont text-[10px] text-white/55">
              Base del creador: ⚡ {Number(row.creator_base_energy).toLocaleString("es-CO")} KD
            </div>
          )}
        </div>
        <label className="font-mont text-[9px] uppercase tracking-[.12em] text-white/55">
          Costo vigente
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-white/10 px-3">
            <Zap size={13} className="text-cyan-300" />
            <input
              type="number"
              min="0"
              max="1000000"
              step="1"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="min-h-11 w-full bg-transparent font-mont text-sm outline-none"
            />
          </div>
        </label>
        <label className="font-mont text-[9px] uppercase tracking-[.12em] text-white/55">
          Motivo del cambio
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo obligatorio"
            className="kd9-input mt-1 min-h-11 w-full px-3 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={save}
          disabled={busy || Number(value) === Number(row.energy_cost)}
          className="kd9-btn-primary min-h-11 rounded-xl px-4 text-[10px] disabled:opacity-40"
        >
          <Save size={13} /> {busy ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </article>
  );
};

const CostsKD9 = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/api/director/costs/overview");
      setData(payload);
      setError("");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = useMemo(
    () => Object.values(data?.groups || {}).reduce((sum, rows) => sum + rows.length, 0),
    [data],
  );

  return (
    <PageShell
      kicker="Director KD9 · Gobierno económico"
      title="CENTRO DE COSTOS KD9"
      desc="Una vista central de los costos vigentes en Energía KD9. Los cambios aplican únicamente a operaciones futuras y no recalculan compras, solicitudes ni movimientos históricos."
      testId="director-costs-kd9"
    >
      {error && <div className="kd9-card mb-4 p-4 text-red-300">{error}</div>}
      <div className="kd9-card mb-5 flex flex-wrap items-center justify-between gap-4 p-4">
        <div>
          <div className="font-cinzel text-lg text-[#ead6aa]">Fuente canónica, sin copias paralelas</div>
          <p className="mt-1 max-w-3xl font-mont text-[10px] leading-relaxed text-white/55">
            Cada fila modifica el registro que usa el backend para la siguiente operación. El Ledger y los registros históricos conservan el costo que tuvieron al momento de ejecutarse.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="rounded-full border border-cyan-400/20 px-3 py-2 font-mont text-[10px] text-cyan-200">
            {total} costos visibles
          </span>
          <button onClick={load} className="kd9-btn-outline min-h-10 rounded-xl px-4 text-[10px]">
            <RefreshCw size={13} /> Actualizar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="kd9-card p-10 text-center">Cargando costos vigentes…</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(data?.groups || {}).map(([group, rows]) => (
            <section key={group}>
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-cinzel text-xl text-[#ead6aa]">{GROUP_LABELS[group] || group}</h2>
                <span className="font-mont text-[9px] uppercase tracking-widest text-white/40">{rows.length} registros</span>
              </div>
              <div className="space-y-2">
                {rows.length
                  ? rows.map((row) => <CostRow key={`${row.domain}:${row.id}`} row={row} onSaved={load} />)
                  : <div className="kd9-card p-5 font-mont text-xs text-white/45">Sin registros.</div>}
              </div>
            </section>
          ))}
        </div>
      )}
    </PageShell>
  );
};

export default CostsKD9;
